import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * Weekly Check-in: 16 questions exactly as specified by the coach.
 * Layout: 2-column form; numbers + 1-10 scales on the left, free-text on the right.
 */
const NUM_FIELDS = [
  { key: "this_week_weight", label: "This week's weight (lb)", step: "0.1" },
  { key: "sleep_hours",     label: "Avg sleep / night (hrs)",  step: "0.1" },
  { key: "weeks_out",       label: "Weeks out from deadline",  step: "1"   },
];
const SCALES = [
  { key: "adherence",          label: "Adherence to the plan (1-10)" },
  { key: "mood",               label: "Mood this week (1-10)" },
  { key: "stress",             label: "Stress level (1-10)" },
  { key: "energy",             label: "Energy levels (1-10)" },
  { key: "training_intensity", label: "Training intensity (1-10)" },
];
const TEXT_FIELDS = [
  { key: "supplements",   label: "Supplements you are taking",  rows: 2 },
  { key: "cardio",        label: "Cardio you did this week",    rows: 2 },
  { key: "workout_split", label: "Workout split",               rows: 2 },
  { key: "appetite",      label: "Appetite",                    rows: 2 },
  { key: "digestion",     label: "Digestion this week",         rows: 2 },
  { key: "recovery",      label: "Recovery",                    rows: 2 },
  { key: "peds",          label: "PEDs you are taking",         rows: 2 },
  { key: "questions",     label: "Questions / concerns before adjustments?", rows: 3 },
];

export default function CheckInsTab({ clientId, user }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/check-ins`);
      setList(data);
    } catch { toast.error("Failed to load check-ins"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const lastWeight = list[0]?.this_week_weight ?? null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      for (const f of NUM_FIELDS) if (form[f.key] !== undefined && form[f.key] !== "") payload[f.key] = Number(form[f.key]);
      for (const f of SCALES)     if (form[f.key] !== undefined && form[f.key] !== "") payload[f.key] = Number(form[f.key]);
      for (const f of TEXT_FIELDS) if (form[f.key]) payload[f.key] = form[f.key];
      await api.post(`/clients/${clientId}/check-ins`, payload);
      toast.success("Check-in submitted");
      setForm({});
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Failed");
    }
    setSaving(false);
  };

  const addNote = async (checkinId, note) => {
    try {
      await api.put(`/clients/${clientId}/check-ins/${checkinId}/note`, { note });
      toast.success("Note saved");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-12">
      {user.role === "client" && (
        <div className="border border-zinc-800 p-6 md:p-10">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <div>
              <div className="font-display text-3xl uppercase">Weekly Check-in</div>
              <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
                Be honest. Adjustments come from your numbers.
              </div>
            </div>
            {lastWeight != null && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Last week's weight</div>
                <div className="font-display text-3xl text-[#FF3B30]">{lastWeight}<span className="text-zinc-500 text-base ml-1">lb</span></div>
              </div>
            )}
          </div>

          <form onSubmit={submit} className="grid lg:grid-cols-2 gap-6">
            {/* LEFT: numbers + scales */}
            <div className="space-y-4">
              <SectionLabel>Numbers</SectionLabel>
              <div className="grid sm:grid-cols-3 gap-3">
                {NUM_FIELDS.map(f => {
                  const { key: k, ...rest } = f;
                  return <NumField key={k} {...rest} value={form[k] ?? ""} onChange={(v) => setForm({...form, [k]: v})} testid={`checkin-${k}`} />;
                })}
              </div>
              <SectionLabel>Scales (1-10)</SectionLabel>
              <div className="space-y-3">
                {SCALES.map(f => {
                  const { key: k, ...rest } = f;
                  return <ScaleField key={k} {...rest} value={form[k] ?? ""} onChange={(v) => setForm({...form, [k]: v})} testid={`checkin-${k}`} />;
                })}
              </div>
            </div>

            {/* RIGHT: text fields */}
            <div className="space-y-4">
              <SectionLabel>This week's details</SectionLabel>
              {TEXT_FIELDS.map(f => {
                const { key: k, ...rest } = f;
                return <TextField key={k} {...rest} value={form[k] ?? ""} onChange={(v) => setForm({...form, [k]: v})} testid={`checkin-${k}`} />;
              })}
            </div>

            <div className="lg:col-span-2 flex justify-end pt-4 border-t border-zinc-800">
              <Button data-testid="checkin-submit" type="submit" disabled={saving} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-12 px-10 text-xs">
                {saving ? "Submitting..." : "Submit Check-in"}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div>
        <div className="font-display text-3xl uppercase mb-4">{user.role === "client" ? "Your History" : "Check-in History"}</div>
        {list.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-12 text-center text-zinc-500 text-sm">No check-ins yet.</div>
        ) : (
          <div className="space-y-px bg-zinc-800 border border-zinc-800">
            {list.map(c => <CheckInCard key={c.id} c={c} canNote={user.role === "trainer"} onSaveNote={addNote} />)}
          </div>
        )}
      </div>
    </div>
  );
}

const SectionLabel = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] border-l-2 border-[#FF3B30] pl-3 py-1">{children}</div>
);

function NumField({ label, value, onChange, testid, step = "1" }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">{label}</label>
      <Input data-testid={testid} type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11" />
    </div>
  );
}

function ScaleField({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className="text-[11px] tracking-[0.2em] uppercase text-zinc-400 flex-1">{label}</label>
      <div className="flex items-center gap-1" data-testid={testid}>
        {[1,2,3,4,5,6,7,8,9,10].map(n => {
          const active = Number(value) === n;
          return (
            <button key={n} type="button" onClick={() => onChange(String(n))}
              data-testid={`${testid}-${n}`}
              className={`w-7 h-9 text-xs font-mono border ${active ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-white"} transition`}>
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, testid, rows = 2 }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">{label}</label>
      <Textarea data-testid={testid} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white" />
    </div>
  );
}

function CheckInCard({ c, canNote, onSaveNote }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(c.trainer_note || "");

  const weightDelta = (c.this_week_weight != null && c.last_week_weight != null)
    ? (c.this_week_weight - c.last_week_weight).toFixed(1) : null;

  const scales = [
    ["adherence", "Adherence"], ["mood", "Mood"], ["stress", "Stress"],
    ["energy", "Energy"], ["training_intensity", "Intensity"],
  ];
  const texts = [
    ["supplements", "Supplements"], ["cardio", "Cardio"], ["workout_split", "Split"],
    ["appetite", "Appetite"], ["digestion", "Digestion"], ["recovery", "Recovery"],
    ["peds", "PEDs"], ["questions", "Questions / concerns"],
  ];

  return (
    <div className="bg-[#0A0A0A] p-6">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">
          {new Date(c.created_at).toLocaleString()}
        </div>
        {c.this_week_weight != null && (
          <div className="text-right">
            <div className="flex items-baseline gap-3 justify-end">
              {c.last_week_weight != null && (
                <div className="text-zinc-500 text-sm line-through font-mono">{c.last_week_weight} lb</div>
              )}
              <div className="font-display text-3xl">{c.this_week_weight}<span className="text-zinc-500 text-base ml-1">lb</span></div>
            </div>
            {weightDelta != null && (
              <div className={`text-[10px] uppercase tracking-[0.25em] mt-1 ${parseFloat(weightDelta) < 0 ? "text-emerald-400" : parseFloat(weightDelta) > 0 ? "text-[#FF3B30]" : "text-zinc-500"}`}>
                {parseFloat(weightDelta) >= 0 ? "+" : ""}{weightDelta} lb
              </div>
            )}
          </div>
        )}
      </div>

      {/* Numeric + scales row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-4 mb-4">
        {c.sleep_hours != null && <Stat k="Sleep (hr)" v={c.sleep_hours} />}
        {c.weeks_out != null && <Stat k="Weeks Out" v={c.weeks_out} />}
        {scales.map(([k, l]) => c[k] != null && <Stat key={k} k={l} v={`${c[k]}/10`} />)}
      </div>

      {/* Text answers */}
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
        {texts.map(([k, l]) => c[k] && (
          <div key={k}>
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-600 mb-1">{l}</div>
            <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{c[k]}</div>
          </div>
        ))}
      </div>

      {/* Coach note */}
      {c.trainer_note && (
        <div className="bg-[#1A1A1A] border-l-2 border-[#FF3B30] p-3 mt-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] mb-1">Coach note</div>
          <p className="text-sm text-zinc-200 whitespace-pre-wrap">{c.trainer_note}</p>
        </div>
      )}
      {canNote && (
        <div className="mt-4">
          {!noteOpen ? (
            <Button onClick={() => setNoteOpen(true)} data-testid={`add-note-${c.id}`} variant="outline" size="sm" className="rounded-none border-zinc-800 bg-transparent text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white">
              {c.trainer_note ? "Edit Coach Note" : "Add Coach Note"}
            </Button>
          ) : (
            <div className="space-y-2">
              <Textarea data-testid={`note-textarea-${c.id}`} rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none bg-black border-zinc-800 text-white" />
              <div className="flex gap-2">
                <Button data-testid={`save-note-${c.id}`} size="sm" onClick={() => { onSaveNote(c.id, note); setNoteOpen(false); }} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-xs uppercase tracking-[0.2em]">Save</Button>
                <Button size="sm" variant="outline" onClick={() => setNoteOpen(false)} className="rounded-none border-zinc-800 bg-transparent text-xs uppercase tracking-[0.2em] hover:bg-zinc-900">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ k, v }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-600">{k}</div>
    <div className="font-mono text-sm text-zinc-200 mt-1">{v}</div>
  </div>
);
